/** Mirrors HyloCore/API/Realtime/RealtimeFrames.swift and SessionProtocol.swift. */
export const webVersion = { name: '0.7.12', build: '1', client: 'web 0.7.12 (1)' } as const;
export const initialProtocol = 7;
export const maxProtocol = 8;
export const capabilities = ['community.v1', 'community.publish-requests.v1', 'support.v1', 'stop.expected_run.v1', 'screen_context.v1', 'web_links.v1', 'resources.jobs.v1', 'resources.studio.v1', 'resources.cards.v1', 'resources.assistants.v1', 'resources.readers.v1'].sort();

/** Mirrors server/src/updates/types.ts and capabilities.ts, including protocol 8/9 and capability gates.
 * Recognize feature invalidations without projecting feature resources into the messaging database.
 * Unknown types must fence and resnapshot; never recognize arbitrary prefixes.
 */
export const coreUpdateTypes = new Set([
  'noop', 'message.new', 'message.edited', 'message.deleted', 'reaction.changed', 'read.changed', 'receipt.changed',
  'conversation.created', 'conversation.updated', 'member.joined', 'member.left', 'member.role', 'me.member_prefs', 'pins.changed',
  'contact.request.received', 'contact.request.sent', 'contact.request.resolved', 'contact.added', 'contact.removed',
  'block.changed', 'account.updated', 'me.updated', 'delivery.changed', 'message.hidden', 'draft.changed', 'transcript.changed', 'conversation.moved', 'project.removed',
]);
export const featureUpdateTypes = new Set([
  'support.incident.changed', 'support.intent.changed',
  'tag.changed', 'tag.deleted', 'tag_group.changed', 'tag_group.deleted', 'tag_links.changed',
  'join_request.received', 'join_request.sent', 'join_request.resolved', 'thread.changed',
  'contact.notes.changed', 'collection.changed', 'collection.deleted', 'collection_link.changed', 'collection_link.deleted',
  'tag_rule.changed', 'tag_rule.deleted', 'tag_suggestion.changed',
  'project.updated', 'project.member.joined', 'project.member.updated', 'project.member.left', 'project.removed',
  'conversation.readers.changed', 'project.readers.changed', 'dashboard.changed', 'call.updated', 'call.signal',
  'studio.item.changed', 'studio.item.removed', 'studio.draft.changed', 'studio.draft.removed',
  'item.changed', 'item.removed', 'item.handoff.changed', 'item.action.changed', 'item.mine.changed', 'item.preferences.changed',
  'calendar.changed', 'calendar.sharing.changed', 'item.reminders.changed', 'label.facet.changed', 'label.value.changed', 'account.labels.changed',
  'resource.cards.changed', 'resource.assistants.changed', 'item.replica.delta', 'item.replica.scope',
  'community.post.changed', 'community.viewer.changed', 'community.activity.changed',
  'chat_import.changed', 'chat_import.removed', 'export.changed', 'export.removed', 'ai.request.changed', 'studio.trial.changed',
  'studio.character.changed', 'studio.character.removed', 'studio.document.changed', 'studio.document.removed',
]);
export const knownUpdateTypes = new Set([...coreUpdateTypes, ...featureUpdateTypes]);

export class SessionProtocol {
  version = initialProtocol;
  private refused: number | undefined;
  update(serverProtocol: unknown): boolean {
    if (typeof serverProtocol !== 'number' || !Number.isInteger(serverProtocol) || serverProtocol < 1) return false;
    const candidate = Math.min(maxProtocol, serverProtocol);
    if (this.refused && candidate < this.refused) this.refused = undefined;
    const next = Math.min(candidate, this.refused ? this.refused - 1 : maxProtocol);
    const changed = next !== this.version; this.version = next; return changed;
  }
  refuse(): boolean { if (this.version < 8) return false; this.refused = this.version; this.version--; return true; }
}
